import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BankAccountData,
  ConnectionStatus,
  PlaidHoldingsData,
  PlaidTransactionsData,
} from "@/util/plaid-util";
import {
  PLAID_STALE_USER_REQUEST,
  fetchPlaidAccounts,
  fetchPlaidChartData,
  fetchPlaidHoldings,
  fetchPlaidTransactions,
  fetchPlaidWealthSnapshot,
} from "./plaid-thunks";
import { WealthChartData } from "@/types/wealth";

export type PlaidState = {
  accounts: BankAccountData[];
  chartData: WealthChartData;
  summary: {
    total_assets: number;
    total_liabilities: number;
    net_worth: number;
  } | null;
  snapshotId: string | null;
  snapshotAsOf: string | null;
  lastPlaidValueDatesByAccountId: Record<string, string | null>;
  holdings: PlaidHoldingsData | null;
  transactions: PlaidTransactionsData | null;
  lastUpdated: number | null;
  connectionStatus: ConnectionStatus;
  connectionError: string | null;
  loading: {
    accounts: boolean;
    chart: boolean;
    holdings: boolean;
    transactions: boolean;
  };
  error: string | null;
  plaidHasNoLinkedAccounts: boolean;
  plaidSnapshotUnavailable: boolean;
};

const initialState: PlaidState = {
  accounts: [],
  chartData: {
    values: [],
    timestamps: [],
  },
  summary: null,
  snapshotId: null,
  snapshotAsOf: null,
  lastPlaidValueDatesByAccountId: {},
  holdings: null,
  transactions: null,
  lastUpdated: null,
  connectionStatus: "idle",
  connectionError: null,
  loading: {
    accounts: false,
    chart: false,
    holdings: false,
    transactions: false,
  },
  error: null,
  plaidHasNoLinkedAccounts: false,
  plaidSnapshotUnavailable: false,
};

const isAbortedRequest = (action: {
  meta?: { aborted?: boolean };
  error?: any;
}) => action.meta?.aborted || action.error?.name === "AbortError";

const hasStaleMeta = (action: { meta?: unknown }): boolean => {
  if (!action.meta || typeof action.meta !== "object") {
    return false;
  }
  return Boolean((action.meta as { stale?: boolean }).stale);
};

const isNoLinkedAccountsError = (message?: string | null): boolean => {
  if (!message) {
    return false;
  }
  const normalized = message.toLowerCase();
  return (
    normalized.includes("no linked") ||
    normalized.includes("no external") ||
    normalized.includes("no account") ||
    normalized.includes("no accounts")
  );
};

const isSnapshotUnavailableError = (message?: string | null): boolean => {
  if (!message) {
    return false;
  }
  const normalized = message.toLowerCase();
  return (
    normalized.includes("(404)") ||
    normalized.includes("(410)") ||
    normalized.includes("not found") ||
    normalized.includes("gone") ||
    normalized.includes("no snapshot") ||
    normalized.includes("not onboarded")
  );
};

const plaidSlice = createSlice({
  name: "plaid",
  initialState,
  reducers: {
    resetPlaidState: () => initialState,
    setLastUpdated: (state, action: PayloadAction<number | null>) => {
      state.lastUpdated = action.payload;
    },
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
    },
    setConnectionError: (state, action: PayloadAction<string | null>) => {
      state.connectionError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaidAccounts.pending, (state) => {
        state.loading.accounts = true;
      })
      .addCase(fetchPlaidAccounts.fulfilled, (state, action) => {
        state.loading.accounts = false;
        state.accounts = action.payload.accounts;
        state.summary = action.payload.summary;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchPlaidAccounts.rejected, (state, action) => {
        state.loading.accounts = false;
        if (
          isAbortedRequest(action) ||
          action.payload === PLAID_STALE_USER_REQUEST ||
          hasStaleMeta(action)
        ) {
          return;
        }
        state.error = action.error.message || "Failed to fetch accounts";
      })
      .addCase(fetchPlaidChartData.pending, (state) => {
        state.loading.chart = true;
        state.error = null;
      })
      .addCase(fetchPlaidChartData.fulfilled, (state, action) => {
        state.loading.chart = false;
        state.chartData = action.payload.chartData;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchPlaidChartData.rejected, (state, action) => {
        state.loading.chart = false;
        if (
          isAbortedRequest(action) ||
          action.payload === PLAID_STALE_USER_REQUEST
        ) {
          return;
        }
        state.error = action.error.message || "Failed to fetch chart data";
      })
      .addCase(fetchPlaidWealthSnapshot.pending, (state) => {
        state.loading.accounts = true;
        state.loading.chart = true;
        state.error = null;
        state.plaidHasNoLinkedAccounts = false;
        state.plaidSnapshotUnavailable = false;
      })
      .addCase(fetchPlaidWealthSnapshot.fulfilled, (state, action) => {
        state.loading.accounts = false;
        state.loading.chart = false;
        state.error = null;
        const accounts = action.payload.accounts || [];
        const requestedAccountIds = action.meta?.arg?.accountIds;
        const isSubsetSnapshotRequest =
          Array.isArray(requestedAccountIds) && requestedAccountIds.length > 0;
        const explicitNoLinkedAccounts =
          action.payload.snapshot_unavailable_reason === "no_linked_accounts";
        const explicitSnapshotUnavailable =
          action.payload.snapshot_unavailable_reason === "snapshot_unavailable";
        // The current snapshot endpoint can return a successful empty payload for
        // users with no linked external accounts.
        const inferredNoLinkedAccounts =
          !explicitSnapshotUnavailable &&
          !isSubsetSnapshotRequest &&
          accounts.length === 0;
        state.accounts = accounts;
        state.chartData = {
          values: action.payload.chart?.values || [],
          timestamps: action.payload.chart?.timestamps || [],
        };
        const lastPlaidValueDates = action.payload.last_plaid_value_dates || {};
        const summary = action.payload.summary || {
          total_assets: 0,
          total_liabilities: 0,
          net_worth: 0,
        };
        state.summary = {
          total_assets: summary.total_assets,
          total_liabilities: summary.total_liabilities,
          net_worth: summary.net_worth,
        };
        state.snapshotId = action.payload.snapshot_id;
        state.snapshotAsOf = action.payload.as_of;
        state.lastPlaidValueDatesByAccountId = lastPlaidValueDates;
        state.plaidHasNoLinkedAccounts =
          explicitNoLinkedAccounts || inferredNoLinkedAccounts;
        state.plaidSnapshotUnavailable = explicitSnapshotUnavailable;
        state.accounts = state.accounts.map((account) => ({
          ...account,
          last_plaid_value_date:
            lastPlaidValueDates[account.account_id] ??
            account.last_plaid_value_date ??
            null,
        }));
        state.lastUpdated = Date.now();
      })
      .addCase(fetchPlaidWealthSnapshot.rejected, (state, action) => {
        state.loading.accounts = false;
        state.loading.chart = false;
        if (
          isAbortedRequest(action) ||
          action.payload === PLAID_STALE_USER_REQUEST
        ) {
          return;
        }
        const errorMessage =
          action.error.message || "Failed to fetch wealth snapshot";
        state.error = errorMessage;
        if (isNoLinkedAccountsError(errorMessage)) {
          state.plaidHasNoLinkedAccounts = true;
          state.plaidSnapshotUnavailable = false;
        } else if (isSnapshotUnavailableError(errorMessage)) {
          state.plaidHasNoLinkedAccounts = false;
          state.plaidSnapshotUnavailable = true;
        } else {
          state.plaidHasNoLinkedAccounts = false;
          state.plaidSnapshotUnavailable = false;
        }
      })
      .addCase(fetchPlaidHoldings.pending, (state) => {
        state.loading.holdings = true;
      })
      .addCase(fetchPlaidHoldings.fulfilled, (state, action) => {
        state.loading.holdings = false;
        state.holdings = action.payload.holdings;
      })
      .addCase(fetchPlaidHoldings.rejected, (state, action) => {
        state.loading.holdings = false;
        if (
          isAbortedRequest(action) ||
          action.payload === PLAID_STALE_USER_REQUEST
        ) {
          return;
        }
        state.error = action.error.message || "Failed to fetch holdings";
      })
      .addCase(fetchPlaidTransactions.pending, (state) => {
        state.loading.transactions = true;
      })
      .addCase(fetchPlaidTransactions.fulfilled, (state, action) => {
        state.loading.transactions = false;
        state.transactions = action.payload.transactions;
      })
      .addCase(fetchPlaidTransactions.rejected, (state, action) => {
        state.loading.transactions = false;
        if (
          isAbortedRequest(action) ||
          action.payload === PLAID_STALE_USER_REQUEST
        ) {
          return;
        }
        state.error = action.error.message || "Failed to fetch transactions";
      });
  },
});

export const {
  resetPlaidState,
  setLastUpdated,
  setConnectionStatus,
  setConnectionError,
} = plaidSlice.actions;

export default plaidSlice.reducer;
